import React from "react";
import { Table, Button, Icon, Dropdown, Input, Header, Label } from "semantic-ui-react";
import { ORIGENES, aptoParaFluido, INSTRUMENTOS } from "../catalogs/instrumentos";
import { calcularCasoB2 } from "../waterBalanceCalc";
import { useWaterBalanceT } from "../useWaterBalanceT";

const ORIGEN_OPTIONS = Object.values(ORIGENES).map((o) => ({ key: o.id, value: o.id, text: o.nombre }));

// Paso 7 — Salidas y almacenamiento. Nuevo, no heredado. La evidencia
// adjunta es lo que evita que el grado quede topeado en 3 (ver
// waterBalanceCalc.gradoDe). Un instrumento no apto para su fluido topa en 3
// el grado de la salida que lo usa (motor de cálculo §2, ver
// estado.js#buildEstadoParaMotor) — acá solo se elige cuál instrumento midió
// cada salida, el tope se calcula al armar el estado para el motor.
export function SalidasYAlmacenamientoStep({ centers, outputs, onChangeSalidas, storage, onChangeAlmacenamiento, instruments, onUploadEvidencia }) {
  const t = useWaterBalanceT();
  const listaSalidas = outputs || [];
  const listaTanques = storage || [];
  const listaInstrumentos = instruments || [];

  const centroOptions = (centers || []).map((c) => ({ key: c.id, value: c.id, text: c.nombre }));
  const instrumentoOptions = [
    { key: "", value: "", text: t("water_balance_no_instrument_option") },
    ...listaInstrumentos.map((i) => ({ key: i.id, value: i.id, text: i.name || i.id })),
  ];

  const actualizarSalida = (idx, campo, val) => onChangeSalidas(listaSalidas.map((s, i) => (i === idx ? { ...s, [campo]: val } : s)));
  const agregarSalida = () => onChangeSalidas([...listaSalidas, { centerId: "", name: "", origin: "estim_sin", instrumentId: "", volumeM3: "", evidence: [] }]);
  const quitarSalida = (idx) => onChangeSalidas(listaSalidas.filter((_, i) => i !== idx));

  const subirEvidencia = async (idx, file) => {
    if (!onUploadEvidencia || !file) return;
    const subido = await onUploadEvidencia(file);
    if (subido) actualizarSalida(idx, "evidence", [...(listaSalidas[idx].evidence || []), subido]);
  };

  const actualizarTanque = (idx, campo, val) => {
    const next = listaTanques.map((t2, i) => {
      if (i !== idx) return t2;
      if (campo === "diametro") return { ...t2, geometry: { ...t2.geometry, diameter: val } };
      return { ...t2, [campo]: val };
    });
    onChangeAlmacenamiento(next);
  };
  const agregarTanque = () => onChangeAlmacenamiento([...listaTanques, { name: "", geometry: { diameter: "" }, initialLevel: "", finalLevel: "" }]);
  const quitarTanque = (idx) => onChangeAlmacenamiento(listaTanques.filter((_, i) => i !== idx));

  return (
    <div className="water-balance-step">
      <h3>{t("water_balance_outputs_title")}</h3>
      <Table celled compact>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>{t("water_balance_col_center")}</Table.HeaderCell>
            <Table.HeaderCell>{t("water_balance_col_name")}</Table.HeaderCell>
            <Table.HeaderCell>{t("water_balance_col_origin")}</Table.HeaderCell>
            <Table.HeaderCell>{t("water_balance_col_instrument")}</Table.HeaderCell>
            <Table.HeaderCell>{t("water_balance_col_volume")}</Table.HeaderCell>
            <Table.HeaderCell>{t("water_balance_col_evidence")}</Table.HeaderCell>
            <Table.HeaderCell></Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {listaSalidas.map((s, idx) => {
            const instrumento = s.instrumentId ? listaInstrumentos.find((i) => i.id === s.instrumentId) : null;
            const instrumentoNoApto = instrumento ? !aptoParaFluido(instrumento.instrumentType, instrumento.fluid) : false;
            return (
              <React.Fragment key={idx}>
                <Table.Row warning={instrumentoNoApto}>
                  <Table.Cell>
                    <Dropdown selection compact options={centroOptions} value={s.centerId} onChange={(_, d) => actualizarSalida(idx, "centerId", d.value)} placeholder={t("water_balance_col_center")} />
                  </Table.Cell>
                  <Table.Cell><Input transparent value={s.name} onChange={(e) => actualizarSalida(idx, "name", e.target.value)} /></Table.Cell>
                  <Table.Cell>
                    <Dropdown selection compact options={ORIGEN_OPTIONS} value={s.origin} onChange={(_, d) => actualizarSalida(idx, "origin", d.value)} />
                  </Table.Cell>
                  <Table.Cell>
                    <Dropdown selection compact options={instrumentoOptions} value={s.instrumentId || ""} onChange={(_, d) => actualizarSalida(idx, "instrumentId", d.value)} />
                  </Table.Cell>
                  <Table.Cell><Input type="number" transparent value={s.volumeM3} onChange={(e) => actualizarSalida(idx, "volumeM3", e.target.value === "" ? "" : Number(e.target.value))} /></Table.Cell>
                  <Table.Cell>
                    {(s.evidence || []).length > 0 && <Label color="green" basic>{t("water_balance_files_count", { n: s.evidence.length })}</Label>}
                    <input type="file" style={{ display: "block", fontSize: 11, marginTop: 4 }} onChange={(e) => subirEvidencia(idx, e.target.files?.[0])} />
                  </Table.Cell>
                  <Table.Cell><Button icon="trash" basic size="tiny" onClick={() => quitarSalida(idx)} /></Table.Cell>
                </Table.Row>
                {instrumentoNoApto && (
                  <Table.Row warning>
                    <Table.Cell colSpan={7}>
                      <Icon name="warning sign" /> {t("water_balance_instrument_unfit_warning", { instrumento: INSTRUMENTOS[instrumento.instrumentType]?.nombre, fluido: instrumento.fluid })}
                    </Table.Cell>
                  </Table.Row>
                )}
              </React.Fragment>
            );
          })}
        </Table.Body>
      </Table>
      <Button basic onClick={agregarSalida}><Icon name="plus" /> {t("water_balance_add_output_button")}</Button>

      <Header as="h3" style={{ marginTop: 30 }}>{t("water_balance_storage_title")}</Header>
      <Table celled compact>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>{t("water_balance_col_tank")}</Table.HeaderCell>
            <Table.HeaderCell>{t("water_balance_col_diameter")}</Table.HeaderCell>
            <Table.HeaderCell>{t("water_balance_col_initial_level")}</Table.HeaderCell>
            <Table.HeaderCell>{t("water_balance_col_final_level")}</Table.HeaderCell>
            <Table.HeaderCell>{t("water_balance_col_delta_inventory")}</Table.HeaderCell>
            <Table.HeaderCell></Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {listaTanques.map((tk, idx) => {
            const puedeCalcular = tk.geometry?.diameter && tk.initialLevel !== "" && tk.finalLevel !== "";
            const delta = puedeCalcular ? calcularCasoB2({ diametro: Number(tk.geometry.diameter), nivelInicial: Number(tk.initialLevel), nivelFinal: Number(tk.finalLevel) }).deltaInventarioM3 : null;
            return (
              <Table.Row key={idx}>
                <Table.Cell><Input transparent value={tk.name} onChange={(e) => actualizarTanque(idx, "name", e.target.value)} /></Table.Cell>
                <Table.Cell><Input type="number" transparent value={tk.geometry?.diameter || ""} onChange={(e) => actualizarTanque(idx, "diametro", e.target.value)} /></Table.Cell>
                <Table.Cell><Input type="number" transparent value={tk.initialLevel} onChange={(e) => actualizarTanque(idx, "initialLevel", e.target.value === "" ? "" : Number(e.target.value))} /></Table.Cell>
                <Table.Cell><Input type="number" transparent value={tk.finalLevel} onChange={(e) => actualizarTanque(idx, "finalLevel", e.target.value === "" ? "" : Number(e.target.value))} /></Table.Cell>
                <Table.Cell>{delta != null ? `${delta.toFixed(2)} m³` : "—"}</Table.Cell>
                <Table.Cell><Button icon="trash" basic size="tiny" onClick={() => quitarTanque(idx)} /></Table.Cell>
              </Table.Row>
            );
          })}
        </Table.Body>
      </Table>
      <Button basic onClick={agregarTanque}><Icon name="plus" /> {t("water_balance_add_tank_button")}</Button>
    </div>
  );
}

export default SalidasYAlmacenamientoStep;
